import React, { useEffect, useRef, useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import {
    Button,
    ModalHeader,
    ModalBody,
    Row,
    Col,
    FormGroup,
    Label,
    InputGroup,
} from 'reactstrap';
import InputMask from 'react-input-mask';
import Rodal from '../../../components/Rodal/Rodal';
import * as yup from 'yup';
import { Field, Form, Input } from '@availity/form';
import './RequestPreOrderEngagementAgreementModal.scss';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useStateSelector } from '../../../store/selectors';
import LocationForm from './LocationForm';
import createNotification from '../../../utils/createNotification';
import { BrokerageServiceRequestPreOrder } from './Models/BrokerageServiceRequestPreOrder';
import Helpers from '../../../utils/helper';
import PracticesForm from './PracticesForm';
import { FormikProps } from 'formik';
import { UserSummaryPracticeDTO } from '../../Practices/practiceDataMapper';

interface RequestPreOrderEngagementAgreementModalProps {
    isVisible: boolean;
    closeModal: () => void;
    brokerage: string;
    fteW2Dvms: number;
    ltmRevenue: number;
    practice: UserSummaryPracticeDTO;
}
const RequestPreOrderEngagementAgreementModal = (
    props: RequestPreOrderEngagementAgreementModalProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    type FormValues = typeof initialValues;
    const formRef = useRef<FormikProps<FormValues> | null>(null);
    const [validationSchema, setValidationSchema] =
        useState<yup.AnyObjectSchema>();
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [isPhoneFieldTouched, setIsPhoneFieldTouched] = useState(false);

    const getDefaultValidationSchema = () =>
        yup.object().shape({
            practiceDba: yup.string().required('Practice DBA is required.'),
            fullLegalNameOfPractice: yup
                .string()
                .required('Full Legal Name is required.'),
            typeOfLegalEntity: yup
                .string()
                .required('Type of Legal Entity is required.')
                .notOneOf([''], 'Please select type of legal entity.'),
            otherTypeOfLegalEntity: yup.string().when('typeOfLegalEntity', {
                is: 'Other',
                then: (schema) =>
                    schema.required(
                        'Please specify other type of legal entity.'
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            numberOfLocations: yup.number().required('This field is invalid.'),
            locations: yup
                .array()
                .of(
                    yup.object().shape({
                        street: yup
                            .string()
                            .required('Street address is required.'),
                        city: yup.string().required('City is required.'),
                        state: yup.string().required('State is required.'),
                        zipCode: yup
                            .string()
                            .test(
                                'is-zip-code',
                                'zip-code-invalid',
                                (value) => {
                                    if (!value || value.trim() === '') {
                                        return true;
                                    }
                                    return (
                                        value.length === 5 &&
                                        /^[0-9]*$/.test(value)
                                    );
                                }
                            )
                            .required('Zip code is required.'),
                        dbaName: yup.string().required('DBA name is required.'),
                        fteDvms: yup
                            .number()
                            .typeError('Must be a number')
                            .required('FTE DVMs is required.')
                            .min(1, 'Must be at least 1'),
                    })
                )
                .min(1, 'At least one location is required.'),
            owners: yup
                .array()
                .of(yup.string().required('Owner is required.'))
                .min(1, 'At least one owner is required.'),
            email: yup.string().required().email(),
            mailingAddress: yup
                .string()
                .required('Mailing Address is required.'),
        });

    const typeOfLegalEntityList = [
        { id: 1, type: 'S-Corp' },
        { id: 2, type: 'C-Corp' },
        { id: 3, type: 'LLC' },
        { id: 4, type: 'Partnership' },
        { id: 5, type: 'Sole Proprietorship' },
        { id: 6, type: 'Other' },
    ];

    const trimPhoneTo10Digits = (phone: string) => {
        return phone.length > 10 ? phone.slice(phone.length - 10) : phone;
    };

    const getDefaultLocations = () => {
        if (
            props.practice?.practiceLocationData &&
            props.practice.practiceLocationData.length > 0
        ) {
            return props.practice.practiceLocationData.map((location) => ({
                street: location.address || '',
                city: location.city || '',
                state: location.state || '',
                zipCode: location.zipCode || '',
                dbaName: location.practiceLocationName || '',
                fteDvms: location.fteDvm || 0,
            }));
        }
        return [
            {
                street: '',
                city: '',
                state: '',
                zipCode: '',
                dbaName: '',
                fteDvms: undefined as number,
            },
        ];
    };

    const initialValues = {
        practiceDba: props.practice?.name,
        fullLegalNameOfPractice: '',
        typeOfLegalEntity: '',
        otherTypeOfLegalEntity: '',
        numberOfLocations: props.practice?.practiceLocationData?.length || 1,
        locations: getDefaultLocations(),
        owners: [summary?.name ?? ''],
        phone: trimPhoneTo10Digits(summary?.phoneNumber ?? ''),
        email: summary?.email ?? '',
        mailingAddress: '',
        practices: [] as number[],
    };

    useBodyScrollLock();

    useEffect(() => {
        setValidationSchema(getDefaultValidationSchema());
    }, []);

    const validatePhone = (phone: string) => {
        phone = Helpers.trimPhoneNumberFromMask(phone);
        const isValid = phone.length === 10 && !Number.isNaN(phone);
        setIsPhoneValid(isValid);
        return isValid;
    };

    const handleNumberOfLocationsChange = (
        event: any,
        values: any,
        setFieldValue: (field: string, value: any) => void
    ) => {
        const newNumberOfLocations = Number(event.target.value);
        const newLocations =
            newNumberOfLocations > values.locations.length
                ? [
                      ...values.locations,
                      ...Array(
                          newNumberOfLocations - values.locations.length
                      ).fill({
                          street: '',
                          city: '',
                          state: '',
                          zipCode: '',
                          dbaName: '',
                          fteDvms: '',
                      }),
                  ]
                : values.locations.slice(0, newNumberOfLocations);

        setFieldValue('locations', newLocations);
    };

    const handleRemoveOwner = (
        index: number,
        values: any,
        setFieldValue: (field: string, value: any) => void
    ) => {
        const newOwners = [
            ...values.owners.slice(0, index),
            ...values.owners.slice(index + 1),
        ];
        setFieldValue('owners', newOwners);
    };

    const handleAddOwner = (
        values: any,
        setFieldValue: (field: string, value: any) => void
    ) => {
        const newOwners = [...values.owners, ''];
        setFieldValue('owners', newOwners);
    };

    const sendPreOrderEngagementAgreementRequest = (
        requestObj: BrokerageServiceRequestPreOrder
    ) => {
        axios
            .post(
                '/api/BrokerageService/RequestPreOrderEngagementAgreement',
                requestObj
            )
            .then((response: any) => {
                if (response.status === 200) {
                    createNotification(
                        'We will contact you with within 24 hours regarding the engagement agreement.',
                        'success'
                    );
                    props.closeModal();
                } else {
                    createNotification(
                        'Something went wrong while processing request',
                        'error'
                    );
                }
            })
            .catch(function (error) {
                createNotification(
                    'An error ocurred while processing request.',
                    'error'
                );
            });
    };

    return (
        <Rodal
            className="request-modal"
            visible={props.isVisible}
            onClose={() => props.closeModal()}
            animation={'fade'}
            showMask={true}>
            <div className="pdetail-header">
                <ModalHeader>
                    Request Engagement Agreement - {props.brokerage} Brokerage
                </ModalHeader>
            </div>

            {summary ? (
                <ModalBody className="request-body">
                    <div className="request-form">
                        <div className="obpbq_row">
                            <Form
                                innerRef={formRef}
                                onSubmit={() => {
                                    setIsPhoneFieldTouched(true);
                                    if (
                                        !validatePhone(
                                            formRef.current.values.phone
                                        )
                                    )
                                        return;
                                    const requestObj: BrokerageServiceRequestPreOrder =
                                        {
                                            practiceId: Number(
                                                props.practice?.id
                                            ),
                                            brokerageType: props.brokerage,
                                            noOfFteDvms: props.fteW2Dvms,
                                            ltmRevenue: props.ltmRevenue,
                                            practiceDba:
                                                formRef.current.values
                                                    .practiceDba,
                                            fullLegalNameOfPractice:
                                                formRef.current.values
                                                    .fullLegalNameOfPractice,
                                            typeOfLegalEntity:
                                                formRef.current.values
                                                    .typeOfLegalEntity ===
                                                'Other'
                                                    ? formRef.current.values
                                                          .otherTypeOfLegalEntity
                                                    : formRef.current.values
                                                          .typeOfLegalEntity,
                                            locations:
                                                formRef.current.values
                                                    .locations,
                                            owners: formRef.current.values
                                                .owners,
                                            phone: formRef.current.values.phone,
                                            email: formRef.current.values.email,
                                            mailingAddress:
                                                formRef.current.values
                                                    .mailingAddress,
                                            practices:
                                                formRef.current.values
                                                    .practices,
                                        };

                                    sendPreOrderEngagementAgreementRequest(
                                        requestObj
                                    );
                                }}
                                initialValues={initialValues}
                                validationSchema={validationSchema}>
                                {({ values, setFieldValue }) => {
                                    return (
                                        <>
                                            <Row>
                                                <Col md={6}>
                                                    <Field
                                                        type="text"
                                                        name="practiceDba"
                                                        label="Practice DBA"
                                                        className="form-input"
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label for="Full Legal Name of Practice">
                                                            Full Legal Name of
                                                            Practice
                                                            <InfoTooltip
                                                                idText="fln-of-practice-tooltip"
                                                                text="Exactly as listed on your state fillings"
                                                                place="top"
                                                            />
                                                        </Label>
                                                        <div className="mb-0">
                                                            <Field
                                                                type="text"
                                                                name="fullLegalNameOfPractice"
                                                                className="form-input"
                                                            />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <h5 className="request-popup-text">
                                                State of Registration
                                            </h5>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>
                                                            Type of Legal Entity
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="typeOfLegalEntity">
                                                            {[
                                                                {
                                                                    id: 0,
                                                                    type: 'Select',
                                                                },
                                                                ...typeOfLegalEntityList,
                                                            ].map((item) => (
                                                                <option
                                                                    value={
                                                                        item.type
                                                                    }
                                                                    key={
                                                                        item.id
                                                                    }>
                                                                    {item.type}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            {values.typeOfLegalEntity ===
                                            'Other' ? (
                                                <Row>
                                                    <Col md={6}>
                                                        <Field
                                                            type="text"
                                                            label="Please specify"
                                                            name="otherTypeOfLegalEntity"
                                                        />
                                                    </Col>
                                                </Row>
                                            ) : (
                                                []
                                            )}

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>
                                                            Number of Locations
                                                        </Label>
                                                        <Input
                                                            type="select"
                                                            name="numberOfLocations"
                                                            defaultValue={
                                                                props.practice
                                                                    ?.practiceLocationData
                                                                    ?.length ||
                                                                1
                                                            }
                                                            onChange={(event) =>
                                                                handleNumberOfLocationsChange(
                                                                    event,
                                                                    values,
                                                                    setFieldValue
                                                                )
                                                            }>
                                                            {[...Array(50)].map(
                                                                (_, index) => {
                                                                    const value =
                                                                        index +
                                                                        1;
                                                                    return (
                                                                        <option
                                                                            value={
                                                                                value
                                                                            }
                                                                            key={
                                                                                value
                                                                            }>
                                                                            {
                                                                                value
                                                                            }
                                                                        </option>
                                                                    );
                                                                }
                                                            )}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            {values.locations.map(
                                                (loc, index) => (
                                                    <LocationForm
                                                        index={index}
                                                    />
                                                )
                                            )}
                                            <h5 className="request-popup-text">
                                                Please list all Owners of the
                                                Practice
                                            </h5>
                                            {values.owners.map(
                                                (owner, index) => (
                                                    <Row
                                                        key={index}
                                                        className="align-items-center">
                                                        <Col xs={11} md={6}>
                                                            <FormGroup className="mb-0">
                                                                <Label>
                                                                    Owner{' '}
                                                                    {index + 1}
                                                                </Label>
                                                                <Field
                                                                    name={`owners[${index}]`}
                                                                    type="text"
                                                                    className="form-control form-input"
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col
                                                            xs={0.5}
                                                            className="d-flex justify-content-end align-items-center mt-3">
                                                            {values.owners
                                                                .length > 1 && (
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faTrashCan
                                                                    }
                                                                    className="delete-button"
                                                                    onClick={() =>
                                                                        handleRemoveOwner(
                                                                            index,
                                                                            values,
                                                                            setFieldValue
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                )
                                            )}

                                            <div className="d-flex">
                                                <Button
                                                    color="primary"
                                                    onClick={() =>
                                                        handleAddOwner(
                                                            values,
                                                            setFieldValue
                                                        )
                                                    }>
                                                    Add Owner
                                                </Button>
                                            </div>
                                            <h5 className="request-popup-text mt-2">
                                                Preferred Contact for Engagement
                                                Related Correspondence
                                            </h5>
                                            <Row>
                                                <Col md={6}>
                                                    <Field
                                                        name="email"
                                                        type="text"
                                                        label="Owner Email"
                                                        className="form-input"
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label for="txtCellNumber">
                                                            Phone
                                                        </Label>
                                                        <InputGroup>
                                                            <div className="input-group-text prepend-addon">
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faPhone
                                                                    }
                                                                />
                                                            </div>
                                                            <InputMask
                                                                className={`form-input form-control phone-field${
                                                                    !isPhoneValid &&
                                                                    isPhoneFieldTouched
                                                                        ? ' is-invalid'
                                                                        : ''
                                                                }`}
                                                                mask="(999) 999-9999"
                                                                maskChar={null}
                                                                name="phone"
                                                                id="txtCellNumber"
                                                                value={
                                                                    values.phone
                                                                }
                                                                required
                                                                onChange={(
                                                                    event
                                                                ) => {
                                                                    validatePhone(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    );
                                                                    setFieldValue(
                                                                        'phone',
                                                                        Helpers.trimPhoneNumberFromMask(
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    );
                                                                }}
                                                                onBlur={() =>
                                                                    setIsPhoneFieldTouched(
                                                                        true
                                                                    )
                                                                }
                                                            />
                                                        </InputGroup>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md={6}>
                                                    <Field
                                                        name="mailingAddress"
                                                        type="text"
                                                        label="Mailing Address"
                                                        className="form-input"
                                                    />
                                                </Col>
                                            </Row>
                                            {summary?.practices.length > 1 && (
                                                <h5 className="request-popup-text mb-1">
                                                    Additional Practices
                                                </h5>
                                            )}
                                            <PracticesForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                selectedPracticeId={Number(
                                                    props.practice?.id
                                                )}
                                            />
                                        </>
                                    );
                                }}
                            </Form>
                        </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center flex-row justify-content-center">
                        <button
                            onClick={props.closeModal}
                            className="btn btn-secondary me-3"
                            style={{ marginRight: '15px' }}>
                            Go Back
                        </button>
                        <button
                            onClick={() => {
                                formRef.current?.submitForm();
                            }}
                            className="btn btn-primary">
                            Submit
                        </button>
                    </div>
                </ModalBody>
            ) : (
                <></>
            )}
        </Rodal>
    );
};

export default RequestPreOrderEngagementAgreementModal;
